import * as signalR from '@microsoft/signalr';
import { useEffect, useState } from 'react';
import { useAuth0 } from "@auth0/auth0-react";
import { recallUpdatedSignal, RecallUpdatedMessage } from '../../hooks/useRecallNotifications';
import { useMainConfig } from '../../modules/config/MainConfigContext';

export default function RecallsHubClient() {
    const { logout } = useAuth0();
    const { config } = useMainConfig();
    const [connection, setConnection] = useState<signalR.HubConnection | null>(null);

    useEffect(() => {
        // Wait for config to load
        if (!config) {
            return;
        }

        const options: signalR.IHttpConnectionOptions = {
            accessTokenFactory: () => {
                const token = localStorage.getItem(process.env.REACT_APP_ACCESS_TOKEN as string);
                return token || ''; // Returning an empty string if the token is not found
            }
        };

        const path = 'recalls-message-broker-hub';
        
        // Always connect through the API URL - the backend handles Azure SignalR negotiation
        // When Azure SignalR is enabled, the backend will negotiate with Azure SignalR Service
        // and redirect the client to the appropriate endpoint
        const baseURL = process.env.REACT_APP_API_URL || '';

        // Ensure the base URL does not end with a slash
        const formattedBaseURL = baseURL.endsWith('/') ? baseURL.slice(0, -1) : baseURL;
        const fullUrl = new URL(`${formattedBaseURL}/${path}`);

        const newConnection = new signalR.HubConnectionBuilder()
            .withUrl(fullUrl.toString(), options)
            .withAutomaticReconnect()
            .build();

        setConnection(newConnection);

        // Register message handler BEFORE starting connection
        // This ensures the handler is always registered, even on automatic reconnects
        // This is critical for Azure SignalR backplane scenarios where reconnections are common
        const handleRecallUpdated = (message: any) => {
            // Map PascalCase from backend to camelCase for frontend
            const mappedMessage: RecallUpdatedMessage = {
                id: message.Id || message.id,
                recallStatus: message.RecallStatus || message.recallStatus,
                reference: message.Reference || message.reference,
                accountName: message.AccountName || message.accountName,
                dateRequested: message.DateRequested || message.dateRequested,
                requestType: message.RequestType || message.requestType,
                requestedBy: message.RequestedBy || message.requestedBy,
                createdBy: message.CreatedBy ?? message.createdBy,
                onBehalfOf: message.OnBehalfOf ?? message.onBehalfOf ?? message.RequestedBy ?? message.requestedBy,
                deliveryMethod: message.DeliveryMethod || message.deliveryMethod,
                deliveryPrice: message.DeliveryPrice || message.deliveryPrice,
                orderNumber: message.OrderNumber || message.orderNumber,
                invoiceAddress: message.InvoiceAddress || message.invoiceAddress,
                deliveryAddress: message.DeliveryAddress || message.deliveryAddress,
                sendToEmail: message.SendToEmail || message.sendToEmail,
                courier: message.Courier || message.courier,
                packaging: message.Packaging || message.packaging,
                trackingUrl: message.TrackingUrl || message.trackingUrl,
                trackingCode: message.TrackingCode || message.trackingCode,
                itemCount: message.ItemCount || message.itemCount,
                cancellationNotes: message.CancellationNotes || message.cancellationNotes,
                canEdit: message.CanEdit !== undefined ? message.CanEdit : (message.canEdit !== undefined ? message.canEdit : false),
                notifyAsNew: message.NotifyAsNew !== undefined ? message.NotifyAsNew : (message.notifyAsNew !== undefined ? message.notifyAsNew : undefined)
            };
            recallUpdatedSignal.value = mappedMessage;
        };

        const handleTestMessage = (message: string) => {
            // You can add toast notification or other handling here
        };

        newConnection.on('RecallUpdated', handleRecallUpdated);
        newConnection.on('TestMessage', handleTestMessage);

        newConnection.start()
            .then(() => {
            })
            .catch(e => console.error('Recalls SignalR connection failed: ', e));
        
        newConnection.onclose((error) => {
        });
        
        newConnection.onreconnecting((error) => {
        });
        
        newConnection.onreconnected((connectionId) => {
            // Handler is already registered
        });

        return () => {
            newConnection.stop();
        };
    }, [logout, config]);

    return null;
}
